import { useEffect, useCallback, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';

interface UseFormPersistenceOptions {
  key: string;
  form: UseFormReturn<any>;
  exclude?: string[];
}

export const useFormPersistence = ({ key, form, exclude = [] }: UseFormPersistenceOptions) => {
  const hasUnsavedChanges = useRef(false);
  const originalValues = useRef<any>(null);

  // Save form data to localStorage
  const saveToStorage = useCallback((data: any) => {
    try {
      const filteredData = { ...data };
      exclude.forEach(field => delete filteredData[field]);
      localStorage.setItem(`form_draft_${key}`, JSON.stringify(filteredData));
    } catch (error) {
      console.warn('Failed to save form data to localStorage:', error);
    }
  }, [key, exclude]);

  // Load form data from localStorage
  const loadFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(`form_draft_${key}`);
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.warn('Failed to load form data from localStorage:', error);
      return null;
    }
  }, [key]);

  // Clear stored data
  const clearStorage = useCallback(() => {
    try {
      localStorage.removeItem(`form_draft_${key}`);
      hasUnsavedChanges.current = false;
    } catch (error) {
      console.warn('Failed to clear form data from localStorage:', error);
    }
  }, [key]);

  // Initialize form with saved data
  useEffect(() => {
    const savedData = loadFromStorage();
    if (savedData && Object.keys(savedData).length > 0) {
      form.reset(savedData);
      hasUnsavedChanges.current = true;
    }
    originalValues.current = form.getValues();
  }, [form, loadFromStorage]);

  // Watch for form changes and auto-save
  useEffect(() => {
    const subscription = form.watch((data) => {
      if (originalValues.current && JSON.stringify(data) !== JSON.stringify(originalValues.current)) {
        hasUnsavedChanges.current = true;
        saveToStorage(data);
      }
    });

    return () => subscription.unsubscribe();
  }, [form, saveToStorage]);

  // Prevent accidental navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges.current) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Mark as saved when form is successfully submitted
  const markAsSaved = useCallback(() => {
    hasUnsavedChanges.current = false;
    clearStorage();
  }, [clearStorage]);

  return {
    markAsSaved,
    clearStorage,
    hasUnsavedChanges: hasUnsavedChanges.current,
  };
};